import BoardCard from '@src/components/Organism/Card/BoardCard';
import BoardListHeader from '@src/components/Organism/Card/BoardListHeader';
import Pagination from '@src/components/Molecules/Pagination';
import PageTemplate from '@src/components/Template/Page';
import { usePagedBoards } from '@src/hooks/useCommunitys';
import useAuth from '@src/hooks/useAuth';
import { IBoardContent } from '@src/interfaces/community';
import { useEffect, useMemo, useState } from 'react';
import Skeleton from '@src/components/LoadingSkeleton';
import keywordAtom from '@src/recoils/atom/keyword';
import { useRecoilState } from 'recoil';
import { metaInfo } from '@src/data/meta';
import Meta from '@src/components/Meta';
import NotFound from '@src/components/Molecules/Card/NotFound';
import styled from 'styled-components';
import { communityKeys } from '@src/keys/communityKey';
import { community as communityApi } from '@src/services/api';
import { dehydrate, QueryClient } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';

/** 첫 화면(검색 없음·1페이지)만 서버에서 미리 채운다. */
export const getServerSideProps: GetServerSideProps = async () => {
  const queryClient = new QueryClient();
  await queryClient.prefetchQuery([...communityKeys.boardPage(''), 0], () =>
    communityApi.getBoards(0, '')
  );
  return {
    props: {
      dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
    },
  };
};

/**
 * 커뮤니티 목록 (Pple Community 디자인).
 * 좌: 타이틀 → 탭/검색 → 컴포저 → 카드 → 페이지네이션 / 우: 사이드바 위젯 3종.
 */
const BoardPage = () => {
  const { title, description, ogImage } = metaInfo.board;
  const [keyword, setKeyword] = useRecoilState(keywordAtom);
  const [searchInput, setSearchInput] = useState(keyword ?? '');
  // 백엔드 board 검색에 category 파라미터가 없어 탭은 클라이언트 필터로 처리한다.
  const [tab, setTab] = useState('');
  const [page, setPage] = useState(0);
  const { fetchProfile } = useAuth();
  const { data: profile } = fetchProfile();

  const { page: result, isLoading } = usePagedBoards(page, keyword);

  useEffect(() => {
    setPage(0);
  }, [keyword]);

  const posts = useMemo(() => {
    const all = (result?.content ?? []) as IBoardContent[];
    return tab ? all.filter((p: any) => p.category === tab) : all;
  }, [result, tab]);

  const totalElements = result?.totalElements;
  const isEmpty = !isLoading && totalElements === 0;

  return (
    <>
      <Meta title={title} description={description} ogImage={ogImage} />
      <PageTemplate
        // 우측 사이드바 없는 단일 컬럼
        hideAside
        maxWidth={1120}
        href={'/community/board'}
        mainText={'커뮤니티'}
      >
        <Main>
            <BoardListHeader
              composerName={profile?.displayName}
              activeTab={tab}
              onTabChange={setTab}
              totalCount={tab ? posts.length : totalElements}
              keyword={searchInput}
              onKeywordChange={setSearchInput}
              onSearch={() => setKeyword(searchInput)}
            />
            {isLoading &&
              [0, 1, 2, 3, 4].map((e) => (
                <Skeleton.LoadingSkeletonCard key={e} />
              ))}
            {!isLoading &&
              posts.map((community: IBoardContent) => (
                <BoardCard key={community.uuid} community={community} />
              ))}
            {isEmpty && <NotFound isNotFound />}
            {!isLoading && !isEmpty && !tab && (
              <Pagination
                page={page}
                totalPages={result?.totalPages ?? 0}
                onChange={(p) => {
                  setPage(p);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            )}
        </Main>
      </PageTemplate>
    </>
  );
};

const Main = styled.div`
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  /* 카드·섹션 사이 간격 — 카드 자체에는 margin을 주지 않는다(이중 적용 방지). */
  gap: 12px;
`;

export default BoardPage;
